import React, { useState, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Box from '@mui/material/Box';
import MuiIconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import FilterIcon from '@mui/icons-material/FilterListOutlined';
import FiltersIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import HospitalsTable from './HospitalsTable';
import HospitalsTableMobile from './HospitalsTableMobile';
import HospitalModal from './HospitalModal';
import FilterHospitalsModal from './FilterHospitalsModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import LoadScreen from '../load-screen';
import Alert from '../shared/Alert';
import Search from '../shared/Search';
import Button from '../shared/Button';
import IconButton from '../shared/IconButton';

import {
  fetchHospitals,
  updateHospital,
  deleteHospital,
  addHospital,
} from '../../actions/adminActions';
import { toggleSidebar } from '../../actions/sidebarActions';

import { getComparator, stableSort } from '../../util/table';

import './hospitals.scss';

const HospitalsTableContainer = (props) => {
  const {
    hospitals = [],
    distributors,
    createHospital,
    updateHospital,
    deleteHospital,
    fetchHospitals,
    withButton,
    toggleSidebar,
    countries,
    companies,
    currentUser,
  } = props;

  const theme = useTheme();

  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [fetching] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showCreateModal, toggleCreateModal] = useState(false);
  const [showUpdateModal, toggleUpdateModal] = useState(false);
  const [showDeleteModal, toggleDeleteModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [selectedHospital, setSelectedHospital] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null);

  const [showFilterModal, toggleFilterModal] = useState(false);
  const [filterState, setFilterState] = useState({ companies: [], countries: [] });

  const onEditClick = (hospital) => {
    setSelectedHospital(hospital);
    toggleUpdateModal(true);
  };

  const onHospitalCreate = async (formObj) => {
    try {
      setLoading(true);

      await createHospital(formObj);
      await fetchHospitals();
      setSuccessMessage('New hospital successfully created');
      toggleCreateModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onHospitalUpdate = async (formObj) => {
    if (selectedHospital) {
      try {
        setLoading(true);

        await updateHospital(selectedHospital?.id, formObj);
        await fetchHospitals();
        setSuccessMessage('Hospital successfully updated');
        toggleUpdateModal(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onDeleteClick = async (id) => {
    setSelectedId(id);
    toggleDeleteModal(true);
  };

  const onDeleteHospital = async () => {
    if (selectedId) {
      try {
        setDeleteLoading(true)
        await deleteHospital(selectedId);
        await fetchHospitals();
        toggleDeleteModal(false);
        setSelectedId(null);
      } catch (err) {
        console.error(err);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const onSubmitFilters = (filters) => {
    setFilterState({
      companies: filters.companies || [],
      countries: filters.countries || [],
    });
    toggleFilterModal(false);
  };

  const onRemoveFilterItem = (key, value) => {
    setFilterState({
      ...filterState,
      [key]: filterState[key].filter((v) => v !== value),
    })
  }

  const filteredHospitals = useMemo(() => {
    let filtered = hospitals;

    if (search) {
      const q = search.toLowerCase();
      filtered = hospitals.filter((h) => h?.name?.toLowerCase()?.includes(q));
    }

    if (filterState?.companies?.length) {
      filtered = filtered?.filter((h) => filterState?.companies?.map((c) => c.id)?.includes(h.company));
    }

    if (filterState?.countries?.length) {
      filtered = filtered?.filter((h) => filterState?.countries?.includes(h.country));
    }

    return filtered;
  }, [hospitals, search, filterState]);

  const list = useMemo(() => {
    let result = filteredHospitals;

    if (orderBy) {
      result = stableSort(
        result,
        getComparator(order, orderBy)
      );
    }

    const _page = page - 1;

    return result
      .slice(
        _page * rowsPerPage,
        _page * rowsPerPage + rowsPerPage
      );
  }, [filteredHospitals, page, rowsPerPage, order, orderBy]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {fetching && <LoadScreen />}
      <Box className="cases-table-container">
        <Box className="table-title-container">
          <Box className="flex-1">
            <Box className="d-flex">
              {withButton && (
                <Box sx={{ display: { sm: 'none' } }}>
                  <Box className="m-r-sm">
                    <MuiIconButton
                      edge="start"
                      color="primary"
                      aria-label="menu"
                      onClick={toggleSidebar}
                    >
                      <MenuIcon />
                    </MuiIconButton>
                  </Box>
                </Box>
              )}

              <Box>
                <Box className="table-title">
                  Hospitals
                  <Box className="badge">{hospitals.length}</Box>
                </Box>

                {!isMobile && (
                  <Box className='table-subtitle'>
                    Add, view or manage hospitals
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box className='d-flex'>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '240px', mr: 2 }}>
              <Search
                value={search}
                placeholder="Search"
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </Box>
            <IconButton
              onClick={() => toggleFilterModal(true)}
              icon={<FilterIcon />}
              sx={{ mr: 2 }}
            />
            <Button
              text='Add Hospital'
              onClick={() => toggleCreateModal(true)}
              icon={<AddIcon color='white' />}
            />
          </Box>
        </Box>
        {hospitals.length ? (
          <Fragment>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              {filterState &&
                (Object.keys(filterState)?.some((key) => filterState[key]?.length)
                  && (
                    <div className="filter-list">
                      <FiltersIcon color="secondary" />
                      {Object.keys(filterState).map((key) => (
                        <div key={key} className="filter-list-item-container">
                          {filterState[key].map((value) => (
                            <div key={`${key}-${value}`} className="filter-list-item">
                              <div />
                              <div className="filter-list-item-value">{key === 'companies' ? value?.name : value}</div>
                              <CloseIcon color="secondary" onClick={() => onRemoveFilterItem(key, value)} />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))
              }
              <HospitalsTable
                hospitals={list}
                distributors={distributors}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalRows={filteredHospitals?.length}
                order={order}
                setOrder={setOrder}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
                companies={companies}
              />
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <HospitalsTableMobile hospitals={list} />
            </Box>
          </Fragment>
        ) : (
          <Box className="empty-block">No Hospitals</Box>
        )}
      </Box>

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteHospital}
        title="Delete Hospital"
        text="Are you sure you want to delete this hospital?"
        cancelText="Cancel"
        submitText="Delete"
        loading={loading}
        variant="error"
      />

      <HospitalModal
        open={showCreateModal}
        onClose={() => toggleCreateModal(false)}
        onSubmit={onHospitalCreate}
        countries={countries}
        loading={loading}
        companies={companies}
        currentUser={currentUser}
      />

      <HospitalModal
        open={showUpdateModal}
        onClose={() => {
          toggleUpdateModal(false);
          setSelectedHospital(undefined);
        }}
        onSubmit={onHospitalUpdate}
        countries={countries}
        mode='update'
        initialValues={selectedHospital}
        loading={loading}
        companies={companies}
        currentUser={currentUser}
      />

      <FilterHospitalsModal
        filter={filterState}
        open={showFilterModal}
        onClose={() => toggleFilterModal(false)}
        onSubmit={onSubmitFilters}
        countries={countries}
        companies={companies}
        currentUser={currentUser}
      />

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteHospital}
        title="Delete Hospital?"
        text="Are you sure you want to delete this hospital? This action cannot be reversed."
        closeText="Cancel"
        submitText="Delete"
        variant="error"
        loading={deleteLoading}
      />

      <Alert
        variant="success"
        message={successMessage}
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </>
  );
};

HospitalsTableContainer.propTypes = {
  hospitals: PropTypes.array,
  distributors: PropTypes.array,
};

const mapStateToProps = (state) => {
  return {
    countries: state.countries.list,
    currentUser: state.user.currentUser,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchHospitals: () => dispatch(fetchHospitals()),
    createHospital: (hospitalData) => dispatch(addHospital(hospitalData)),
    updateHospital: (id, hospitalData) => dispatch(updateHospital(id, hospitalData)),
    deleteHospital: (id) => dispatch(deleteHospital(id)),
    toggleSidebar: () => dispatch(toggleSidebar()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(HospitalsTableContainer);
